// R10-B1: following a link to what's already open (a bell item, a toast, a push, a search hit)
// goes to it again and lights it. The router treats a navigation to the address it's on as
// nothing new, so each followed link carries its own number in the history entry's state: the
// conversation reads it to go to its message again. The same address replaces its entry (Back
// still leaves the page); another address adds one.
import { type AnyRouter, useRouterState } from "@tanstack/react-router";

declare module "@tanstack/react-router" {
  interface HistoryState {
    /** A followed link's own number (lib/jump.ts). */
    jump?: number;
  }
}

let last = 0;

/** The history state a followed link carries: a number never used before in this tab (a time,
 *  so an entry kept from before a reload never matches it). */
export function jumpState(): { jump: number } {
  last = Math.max(Date.now(), last + 1);
  return { jump: last };
}

/** Two in-app addresses name the same place: path, query (in any order) and hash. */
export function sameAddress(a: string, b: string): boolean {
  try {
    const x = new URL(a, "http://app");
    const y = new URL(b, "http://app");
    const query = (u: URL) =>
      [...u.searchParams]
        .map(([k, v]) => `${k}=${v}`)
        .sort()
        .join("&");
    return (
      decodeURI(x.pathname) === decodeURI(y.pathname) && x.hash === y.hash && query(x) === query(y)
    );
  } catch {
    return false;
  }
}

/** Follow an in-app link (a path with its query) as a new jump. */
export function follow(router: AnyRouter, href: string): Promise<void> {
  const same = sameAddress(router.state.location.href, href);
  return router.navigate({ href, state: jumpState(), replace: same });
}

/** The number the current entry's link carried; undefined for an address not reached by one. */
export function useJumpNonce(): number | undefined {
  return useRouterState({ select: (s) => s.location.state.jump });
}
